import { initArtistBio } from "./artist-bio";
import { initAccordions } from "./accordions";

// Extend Window interface for global spektrix function
declare global {
  interface Window {
    initSpektrixForms?: (container?: Element | Document) => void;
  }
}

// LocalStorage cache with 20-minute expiration
const CACHE_KEY_PREFIX = "modalCache_3_";
const CACHE_TTL_MS = 20 * 60 * 1000; // 20 minutes

interface CacheEntry {
  content: string;
  timestamp: number;
}

function getCachedContent(url: string): string | null {
  const key = CACHE_KEY_PREFIX + url;
  const stored = localStorage.getItem(key);
  if (!stored) return null;

  try {
    const entry: CacheEntry = JSON.parse(stored);
    if (Date.now() - entry.timestamp < CACHE_TTL_MS) {
      return entry.content;
    }
    // Expired, remove it
    localStorage.removeItem(key);
  } catch {
    localStorage.removeItem(key);
  }
  return null;
}

function setCachedContent(url: string, content: string): void {
  const key = CACHE_KEY_PREFIX + url;
  const entry: CacheEntry = { content, timestamp: Date.now() };
  try {
    localStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // localStorage full or unavailable, ignore
  }
}

function initShowModal() {
  // The service owns show dialogs when its theme bridge is configured.
  if ((window as any).ShortlistConfig?.site === "push") return;
  const modal = document.getElementById("show-modal") as HTMLDialogElement;
  if (!modal) return;

  const modalContent = modal.querySelector(".show-modal-content") as HTMLDivElement;

  function preloadContent(url: string) {
    if (!getCachedContent(url)) {
      updateActions(true);
      fetch(`${url}?bare=1`)
        .then((response) => response.text())
        .then((data) => {
          setCachedContent(url, data);
        })
        .catch(() => {
          // Don't cache errors
        });
    }
  }

  function updateActions(isActive: boolean, currentShowIndex?: number, totalShows?: number) {
    const prevButton = modal.querySelector(".button-prev") as HTMLButtonElement;
    const nextButton = modal.querySelector(".button-next") as HTMLButtonElement;

    if (prevButton && nextButton) {
      if (isActive) {
        prevButton.setAttribute("disabled", "true");
        nextButton.setAttribute("disabled", "true");
      } else {
        if (currentShowIndex === 0) {
          prevButton.setAttribute("disabled", "true");
        } else {
          prevButton.removeAttribute("disabled");
        }

        if (currentShowIndex === totalShows - 1) {
          nextButton.setAttribute("disabled", "true");
        } else {
          nextButton.removeAttribute("disabled");
        }
      }
    }
  }

  function openModal(event: Event) {
    // const isMobile = window.matchMedia("(max-width: 1024px)").matches;
    const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;
    const link = (event.target as HTMLElement).closest("a[data-modal-url]") as HTMLAnchorElement;
    const isToday = link?.getAttribute("data-today") === "true";
    const modalDay = link?.getAttribute("data-modal-day");
    const linkHash = link?.hash || "";
    // add loading class to modal
    modal.classList.add("loading");

    let currentUrl = link?.getAttribute("data-modal-url") || "";
    modal.setAttribute("data-current-url", currentUrl);
    updateUrl(currentUrl.replace(window.location.origin, ""));
    resetButtons();

    modal.showModal();

    const availableModalLinks = document.querySelectorAll(
      isToday
        ? "#today a[data-modal-url]"
        : modalDay
          ? `.program-daily-day#${CSS.escape(modalDay)} h4 a[data-modal-url]`
          : ".show-list-item:not(.show-list-item--ad):not(.show-list-item--hidden) a[data-modal-url]"
    );
    const currentShowIndex = Array.from(availableModalLinks).findIndex(
      (link) => link.getAttribute("data-modal-url") === currentUrl
    );

    // Trigger reflow before adding class to ensure transition works
    modal.offsetHeight;
    modal.classList.add("visible");
    document.documentElement.style.overflow = "hidden";

    const setModalContent = (data: string) => {
      modal.classList.remove("loading");
      if (modal.getAttribute("data-current-url") === currentUrl) {
        modalContent.innerHTML = data;
        initAccordions();
        initAnchors();
        initArtistBio(modalContent);

        // Initialize spektrix forms in modal content
        if (typeof window.initSpektrixForms === 'function') {
          window.initSpektrixForms(modalContent);
        }

        if (linkHash) {
          requestAnimationFrame(() => scrollToHash(linkHash));
        }
      }
    };

    const cachedContent = getCachedContent(currentUrl);
    if (cachedContent) {
      setModalContent(cachedContent);
    } else {
      fetch(`${currentUrl}?bare=1`)
        .then((response) => response.text())
        .then((data) => {
          setCachedContent(currentUrl, data);
          setModalContent(data);
        })
        .catch(() => {
          modalContent.innerHTML = "<p>Error loading content. Please try again.</p>";
        });
    }

    updateActions(false, currentShowIndex, availableModalLinks.length);

    // Close button
    const closeButton = modal.querySelector(".button-close") as HTMLButtonElement;
    closeButton.focus();
    closeButton.addEventListener("click", closeModal);
    modal.addEventListener("keydown", handleEscapeKey);

    modal.addEventListener("click", (event) => {
      if (event.target === modal) {
        closeModal();
      }
    });

    // New tab button
    const newTabButton = modal.querySelector(".button-tab") as HTMLButtonElement;
    newTabButton.addEventListener("click", () => {
      window.open(link?.href || currentUrl, "_blank");
    });

    // Prev and next buttons
    const prevButton = modal.querySelector(".button-prev") as HTMLButtonElement;
    const nextButton = modal.querySelector(".button-next") as HTMLButtonElement;
    const hideControllers =
      link?.getAttribute("data-modal-controllers") === "false" ||
      availableModalLinks.length < 2;

    if (hideControllers) {
      prevButton.style.display = "none";
      nextButton.style.display = "none";
    } else {
      prevButton.style.display = "flex";
      nextButton.style.display = "flex";

      if (prevButton && nextButton) {
        const prevShow =
          currentShowIndex > 0
            ? (availableModalLinks[currentShowIndex - 1] as HTMLLinkElement)
            : null;
        const nextShow =
          currentShowIndex < availableModalLinks.length - 1
            ? (availableModalLinks[currentShowIndex + 1] as HTMLLinkElement)
            : null;

        prevButton.addEventListener("click", () => {
          if (prevShow) {
            modalContent.innerHTML = "";
            prevShow.click();
            updateActions(false, currentShowIndex - 1, availableModalLinks.length);
            currentUrl = prevShow.getAttribute("data-modal-url") || "";
            initAnchors();
          }
        });

        nextButton.addEventListener("click", () => {
          if (nextShow) {
            modalContent.innerHTML = "";
            nextShow.click();
            updateActions(false, currentShowIndex + 1, availableModalLinks.length);
            currentUrl = nextShow.getAttribute("data-modal-url") || "";
            initAnchors();
          }
        });
      }
    }
  }

  function scrollToHash(hash: string) {
    const targetId = hash.replace(/^#/, "");
    if (!targetId) return;
    const content = modal.querySelector(".show-modal-content") as HTMLElement;
    const targetSection = modal.querySelector(`#${CSS.escape(targetId)}`) as HTMLElement;
    if (!content || !targetSection) return;

    const modalContentRect = content.getBoundingClientRect();
    const targetRect = targetSection.getBoundingClientRect();
    const scrollOffset = targetRect.top - modalContentRect.top + content.scrollTop;

    content.scrollTo({
      top: scrollOffset,
      behavior: "smooth",
    });
  }

  // Ticket anchor
  function initAnchors() {
    modal.querySelectorAll("a[href^='#']").forEach((anchor) => {
      anchor.addEventListener("click", (event) => {
        event.preventDefault();
        const targetId = anchor.getAttribute("href")?.split("#")[1];
        if (targetId) {
          scrollToHash(`#${targetId}`);
        }
      });
    });
  }

  function closeModal() {
    modal.classList.remove("visible");

    const onTransitionEnd = () => {
      modal.removeEventListener("transitionend", onTransitionEnd);
      modalContent.innerHTML = "";
      modal.close();
      updateUrl("");
      modal.setAttribute("data-current-url", "");
      updateActions(true);
      document.documentElement.style.overflow = "auto";
    };

    modal.addEventListener("transitionend", onTransitionEnd);

    resetButtons();
    modal.removeEventListener("keydown", handleEscapeKey);
  }

  function resetButtons() {
    const closeButton = modal.querySelector(".button-close") as HTMLButtonElement;
    const prevButton = modal.querySelector(".button-prev") as HTMLButtonElement;
    const nextButton = modal.querySelector(".button-next") as HTMLButtonElement;
    const newTabButton = modal.querySelector(".button-tab") as HTMLButtonElement;
    closeButton.replaceWith(closeButton.cloneNode(true));
    prevButton.replaceWith(prevButton.cloneNode(true));
    nextButton.replaceWith(nextButton.cloneNode(true));
    newTabButton.replaceWith(newTabButton.cloneNode(true));
  }

  function handleEscapeKey(event: KeyboardEvent) {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closeModal();
    }
  }

  const modalLinks = document.querySelectorAll(
    "a[data-modal-url]"
  ) as NodeListOf<HTMLAnchorElement>;

  modalLinks.forEach((link) => {
    const url = link.getAttribute("data-modal-url") || "";
    link.addEventListener("mouseenter", () => {
      if (url) {
        preloadContent(url);
      }
    });

    link.addEventListener(
      "click",
      (event) => {
        if (!event.ctrlKey && !event.metaKey) {
          event.preventDefault();
          event.stopPropagation();
          openModal(event);
        }
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
          event.stopPropagation();
          window.open(link.href, "_blank");
        }
      },
      { capture: true }
    );
  });

  function updateUrl(urlQuery: string) {
    if (urlQuery !== "") {
      window.history.replaceState(null, document.title, urlQuery);
    } else {
      window.history.replaceState(null, document.title, window.location.origin);
    }
  }
}

initShowModal();
